import { AuthUiProvider } from "./context";
import { detectLocale } from "./i18n";
import { AccountPage } from "./pages/account-page";
import { ConsentPage, ForgotPasswordPage, MagicLinkConfirmationPage, MagicLinkRequestPage, ResetPasswordPage, SignInPage, SignUpPage, VerifyEmailPage } from "./pages/auth-pages";
import { PrivacyPage, TermsPage } from "./pages/legal-pages";
import type { AuthLocale, AuthPageProps, AuthUiProviderProps, ConsentRequest } from "./types";

export const AUTH_UI_ROUTES = [
  "/sign-in",
  "/sign-up",
  "/forgot-password",
  "/reset-password",
  "/verify-email",
  "/magic-link",
  "/consent",
  "/account",
  "/privacy",
  "/terms"
] as const;

export type AuthUiRoute = (typeof AUTH_UI_ROUTES)[number];

export interface AuthUiRouterProps extends AuthPageProps {
  path: string;
  consentRequest?: ConsentRequest | undefined;
}

export function normalizeAuthPath(path: string): string {
  const normalized = `/${path.split("?")[0]?.split("#")[0]?.replace(/^\/+|\/+$/g, "") ?? ""}`;
  return normalized === "/" ? "/sign-in" : normalized;
}

export function AuthUiRouter({ path, searchParams = new URLSearchParams(), consentRequest }: AuthUiRouterProps) {
  switch (normalizeAuthPath(path)) {
    case "/sign-in": return <SignInPage searchParams={searchParams} />;
    case "/sign-up": return <SignUpPage searchParams={searchParams} />;
    case "/forgot-password": return <ForgotPasswordPage searchParams={searchParams} />;
    case "/reset-password": return <ResetPasswordPage searchParams={searchParams} />;
    case "/verify-email": return <VerifyEmailPage searchParams={searchParams} />;
    case "/magic-link": return searchParams.has("token") ? <MagicLinkConfirmationPage searchParams={searchParams} /> : <MagicLinkRequestPage searchParams={searchParams} />;
    case "/consent": return <ConsentPage request={consentRequest} />;
    case "/account": return <AccountPage searchParams={searchParams} />;
    case "/privacy": return <PrivacyPage />;
    case "/terms": return <TermsPage />;
    default: return <SignInPage searchParams={searchParams} />;
  }
}

export interface AuthUiAppProps extends Omit<AuthUiProviderProps, "children"> {
  path?: string | undefined;
  searchParams?: URLSearchParams | undefined;
  consentRequest?: ConsentRequest | undefined;
}

export function AuthUiApp({ api, client, path, searchParams, consentRequest, initialLocale, onNavigate }: AuthUiAppProps) {
  const resolvedPath = path ?? (typeof window === "undefined" ? "/sign-in" : window.location.pathname);
  const resolvedSearch = searchParams ?? new URLSearchParams(typeof window === "undefined" ? "" : window.location.search);
  const providerProps = { api, client, ...(initialLocale ? { initialLocale } : {}), ...(onNavigate ? { onNavigate } : {}) };
  const routerProps = { path: resolvedPath, searchParams: resolvedSearch, ...(consentRequest ? { consentRequest } : {}) };
  return <AuthUiProvider {...providerProps}><AuthUiRouter {...routerProps} /></AuthUiProvider>;
}

export function localeFromRequest(request: Request): AuthLocale {
  const language = request.headers.get("accept-language") ?? "";
  return detectLocale(language.split(",").map((entry) => entry.trim().split(";")[0] ?? ""));
}
